// Two-tier cookie-consent banner (GDPR Art. 6(1)(f) / §25 TTDSG).
//
// PostHog is initialised in src/main.tsx at module load and captures a BASIC,
// anonymous, cookie-free reach measurement (page views, visitor counts) for
// EVERY visitor by default — persistence:"memory" + disable_persistence:true
// write NOTHING to the device, so §25 TTDSG is not triggered and no consent is
// required for that tier (same basis Plausible/Fathom rely on).
//
// This banner is the opt-in gate for the PERSISTENT tier only:
//   - Accept  → cookie sportfund_consent=granted (1yr) → switch persistence to
//               localStorage+cookie (cross-session identity + funnel tracing) +
//               turn session recording on.
//   - Decline → cookie sportfund_consent=denied (1yr) → opt out of all capture
//               and wipe any stale PostHog storage.
// Both choices are equally weighted (no dark pattern). Declining changes nothing
// for the user except analytics — no essential site function depends on PostHog.
//
// The banner renders ONLY when no decision cookie exists yet, so a returning
// visitor is never nagged.

import { useEffect, useRef, useState } from 'react';
import posthog from 'posthog-js';

const CONSENT_COOKIE = 'sportfund_consent';
const ONE_YEAR = 60 * 60 * 24 * 365;

// Exposed so any other fixed-bottom UI (e.g. DemoMissionPage's mobile sticky
// CTA) can offset itself above this banner instead of being covered by it —
// this banner is z-[100] and full-width, so anything else pinned to the
// viewport bottom is otherwise hidden underneath it for exactly the
// first-time visitors who see it. Ported from mystudyfund's ConsentBanner.tsx.
const HEIGHT_VAR = '--consent-banner-height';

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return m ? decodeURIComponent(m[1]) : null;
}

function writeCookie(name: string, value: string) {
  document.cookie = `${name}=${value}; Max-Age=${ONE_YEAR}; Path=/; SameSite=Lax`;
}

const COPY = {
  text: 'We measure basic, anonymous, cookie-free reach stats (nothing is stored on your device). “Accept” additionally allows cookies for improved analytics (e.g. cross-session insights and session replay, PostHog · EU-hosted). “Decline” turns all analytics off.',
  accept: 'Accept',
  decline: 'Decline',
  more: 'Privacy policy',
};

export default function ConsentBanner() {
  // Start hidden; decide on the client after mount so we never render during
  // SSR/prerender (avoids hydration mismatch) or for a visitor who already chose.
  const [visible, setVisible] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Skip during the Puppeteer prerender pass (build-time static snapshot):
    // it executes client effects, so without this guard the banner would be
    // baked into every prerendered page's HTML and flash for returning visitors
    // before hydration corrects it. Headless Chrome sets navigator.webdriver.
    if (typeof navigator !== 'undefined' && navigator.webdriver) return;
    if (readCookie(CONSENT_COOKIE)) return; // already decided
    setVisible(true);
  }, []);

  useEffect(() => {
    if (!visible) {
      document.documentElement.style.setProperty(HEIGHT_VAR, '0px');
      return;
    }
    const el = bannerRef.current;
    if (!el) return;
    const publish = () => document.documentElement.style.setProperty(HEIGHT_VAR, `${el.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty(HEIGHT_VAR, '0px');
    };
  }, [visible]);

  if (!visible) return null;

  function accept() {
    writeCookie(CONSENT_COOKIE, 'granted');
    try {
      // Upgrade from the default cookieless/anonymous layer to PERSISTENT
      // analytics now that we have consent: on-device storage (cross-session
      // identity + funnel tracing) and session recording on. Before this,
      // posthog kept everything in memory (persistence:"memory" in main.tsx).
      posthog.set_config({
        persistence: 'localStorage+cookie',
        disable_persistence: false, // now allowed to write to the device
      });
      posthog.opt_in_capturing(); // no-op unless a prior "denied" opted out
      posthog.startSessionRecording?.();
    } catch {
      /* posthog not initialised (no token) — nothing to opt into */
    }
    setVisible(false);
  }

  function decline() {
    writeCookie(CONSENT_COOKIE, 'denied');
    try {
      // Stay opted out + in-memory. Clear any stale posthog storage FIRST, then
      // opt out — reset() clears the persisted opt-out flag, so opting out after
      // the reset is what makes the decision stick across reloads.
      posthog.reset(true);
      posthog.opt_out_capturing();
      // Init already ran this page load with flags enabled (no cookie yet), so
      // opting out is not enough — stop the flag poller too, or a live decline
      // keeps talking to /flags/ until the next reload.
      posthog.featureFlags?.setReloadingPaused?.(true);
    } catch {
      /* no-op */
    }
    setVisible(false);
  }

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-2px_12px_rgba(0,0,0,0.06)] backdrop-blur"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-slate-600">
          {COPY.text}{' '}
          <a href="/privacy" className="underline hover:text-slate-900">
            {COPY.more}
          </a>
        </p>
        <div className="flex flex-none gap-2">
          <button
            type="button"
            onClick={decline}
            className="rounded-md border border-slate-300 bg-white px-4 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            {COPY.decline}
          </button>
          <button
            type="button"
            onClick={accept}
            className="rounded-md bg-primary/90 px-4 py-1.5 text-sm font-semibold text-white hover:bg-blue-800"
          >
            {COPY.accept}
          </button>
        </div>
      </div>
    </div>
  );
}
